/**
 * Pure helpers for dragging chats around the sidebar (pin / reorder / move to
 * a folder). The gesture itself lives in `components/SidebarDnd.tsx`; everything
 * here is DOM-free (or takes a minimal element shape) so it can be unit tested.
 */

/** How long a finger must rest on a chat before it is picked up. */
export const DRAG_HOLD_MS = 380;
/** Finger travel that cancels a pending hold (the user is scrolling instead). */
export const DRAG_HOLD_TOLERANCE_PX = 10;
/** Mouse travel that starts a drag (mouse needs no hold). */
export const DRAG_MOUSE_THRESHOLD_PX = 6;
/** Travel while holding (menu open) after which the chat lifts off and the drag starts. */
export const DRAG_LIFT_PX = 8;
/** Distance from the scroller's edge where auto-scroll kicks in. */
export const AUTOSCROLL_EDGE_PX = 56;
export const AUTOSCROLL_MAX_SPEED_PX = 14;
/** Upper bound of remembered pinned ids so the stored order cannot grow forever. */
export const PINNED_ORDER_MAX = 200;

export type DropTarget =
  | { kind: 'pinned-item'; id: string; after: boolean }
  | { kind: 'pinned-zone' }
  /** Where a pinned chat is dropped to unpin it. */
  | { kind: 'unpin-zone' }
  | { kind: 'folder'; folderId: string }
  /** Between folders: reorder a dragged folder before / after `id`. */
  | { kind: 'folder-slot'; id: string; after: boolean };

export interface ChatDragSource {
  kind?: 'chat';
  id: string;
  folderId: string | null;
  pinned: boolean;
}

export interface FolderDragSource {
  kind: 'folder';
  id: string;
}

export type DragSource = ChatDragSource | FolderDragSource;

export function sameDropTarget(a: DropTarget | null, b: DropTarget | null): boolean {
  if (a === b) return true;
  if (!a || !b || a.kind !== b.kind) return false;
  if (a.kind === 'pinned-item' && b.kind === 'pinned-item') {
    return a.id === b.id && a.after === b.after;
  }
  if (a.kind === 'folder' && b.kind === 'folder') return a.folderId === b.folderId;
  if (a.kind === 'folder-slot' && b.kind === 'folder-slot') return a.id === b.id && a.after === b.after;
  return true;
}

/** Whether the pointer is in the lower half of a row (drop *after* it). */
export function isLowerHalf(pointerY: number, rect: { top: number; height: number }): boolean {
  return pointerY > rect.top + rect.height / 2;
}

/** Minimal element surface the drop-target lookup needs (satisfied by HTMLElement). */
export interface DropElement {
  closest(selector: string): DropElement | null;
  dataset: Record<string, string | undefined>;
  getBoundingClientRect(): { top: number; height: number };
}

/**
 * Work out what is under the pointer. Rows opt in with data attributes:
 *  - `data-dnd-conv` + `data-dnd-pinned="1"` – a chat inside the pinned group
 *  - `data-dnd-pin-zone` – the pinned group / the "drop to pin" placeholder
 *  - `data-dnd-folder="<id>"` – a folder (header and its chats)
 */
export function resolveDropTarget(
  el: DropElement | null,
  pointerY: number,
  source: DragSource,
): DropTarget | null {
  if (!el) return null;

  if (source.kind === 'folder') {
    // A dragged folder only ever lands between folders (they are collapsed meanwhile,
    // so a folder's box is just its header).
    const folder = el.closest('[data-dnd-folder]');
    const id = folder?.dataset.dndFolder;
    if (!folder || !id || id === source.id) return null;
    return { kind: 'folder-slot', id, after: isLowerHalf(pointerY, folder.getBoundingClientRect()) };
  }

  const row = el.closest('[data-dnd-conv]');
  if (row && row.dataset.dndPinned === '1') {
    const id = row.dataset.dndConv;
    if (id && id !== source.id) {
      return { kind: 'pinned-item', id, after: isLowerHalf(pointerY, row.getBoundingClientRect()) };
    }
    // Hovering over the dragged row itself: keep the pinned order untouched.
    if (id === source.id) return null;
  }

  const folder = el.closest('[data-dnd-folder]');
  if (folder) {
    const folderId = folder.dataset.dndFolder;
    if (!folderId || folderId === source.folderId) return null;
    return { kind: 'folder', folderId };
  }

  // A chat that is already pinned is reordered by its rows only; the pin zone
  // would just throw it to the end by accident. It can be dropped on the unpin zone instead.
  if (source.pinned) return el.closest('[data-dnd-unpin-zone]') ? { kind: 'unpin-zone' } : null;
  if (el.closest('[data-dnd-pin-zone]')) return { kind: 'pinned-zone' };
  return null;
}

/** `ids` with `sourceId` moved right before / after `targetId` (unchanged if either is unknown). */
export function placeBeside(ids: string[], sourceId: string, targetId: string, after: boolean): string[] {
  if (sourceId === targetId || !ids.includes(sourceId) || !ids.includes(targetId)) return ids;
  const without = ids.filter((id) => id !== sourceId);
  const index = without.indexOf(targetId) + (after ? 1 : 0);
  without.splice(index, 0, sourceId);
  return without;
}

/**
 * New pinned order after dropping `sourceId` onto `target`.
 * `order` may be stale (missing the target or the source): unknown targets
 * fall back to "end of the list".
 */
export function placeInPinnedOrder(
  order: string[],
  sourceId: string,
  target: Extract<DropTarget, { kind: 'pinned-item' | 'pinned-zone' }>,
): string[] {
  const without = order.filter((id) => id !== sourceId);
  let index = without.length;
  if (target.kind === 'pinned-item') {
    const targetIndex = without.indexOf(target.id);
    if (targetIndex !== -1) index = targetIndex + (target.after ? 1 : 0);
  }
  without.splice(index, 0, sourceId);
  return without.slice(0, PINNED_ORDER_MAX);
}

/** Pixels to scroll this frame: negative up, positive down, 0 away from the edges. */
export function autoScrollDelta(
  pointerY: number,
  top: number,
  bottom: number,
  edge = AUTOSCROLL_EDGE_PX,
  maxSpeed = AUTOSCROLL_MAX_SPEED_PX,
): number {
  if (bottom - top <= edge * 2) return 0;
  if (pointerY < top + edge) {
    const depth = Math.min(1, (top + edge - pointerY) / edge);
    return -Math.ceil(depth * maxSpeed);
  }
  if (pointerY > bottom - edge) {
    const depth = Math.min(1, (pointerY - (bottom - edge)) / edge);
    return Math.ceil(depth * maxSpeed);
  }
  return 0;
}
